import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import Swal from 'sweetalert2';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { IconsService } from 'src/app/services/icons.service';
import { SearchService } from 'src/app/services/search.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * OrdiniParzialiListComponent — lista ordini parziali in lavorazione
 * (vs storico). Route `/ordini-parziali`. Click row → modale
 * `<app-ordini-parziali-dettagli>` (Chunk E.2).
 *
 * Brand color: viola ($partial = #8700f5) coerente con storico-parziali.
 *
 * Phase 1.5 / v2.8.0 — TS cleaned up (Chunk E.1):
 *   • dropped kendo `GridDataResult` + `PageChangeEvent` (no more
 *     kendo-grid),
 *   • dropped legenda accordion state (`isLegendaOpen`, `legendaItems`,
 *     `toggleLegenda`, `openLegenda`, `closeLegenda`) — replaced by a
 *     persistent purple chip in the header,
 *   • dropped ~20 console.log + ~15 dead state fields (gridView, value,
 *     indeterminate, min, max, ordiniParziali, sameIndex, id, date,
 *     timer, refreshInterval, dateBy, searchTermLocalStorage,
 *     booleanForSpecialSearch, gridDataInifinity, searchParam — plus
 *     subscriber: Subscription[] replaced with takeUntilDestroyed),
 *   • dropped `loadProducts` + `pageChange` (kendo paging only),
 *   • dropped `isInWork()` — heavy async guard logic that was never
 *     called from this component's template (verified by grep across
 *     src/app). Legacy copies still exist in assegna-ordine-list,
 *     preparazione-ordini-detaggli, order-details — each component
 *     has its own local copy, no cross-component dep.
 *   • dropped `onDateSelect` (single-line setter never called from
 *     the new template — date filter handled by applyDateFilter()),
 *   • dropped `reloadFunction` alias of `getOrdineParziali`,
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: load partial orders, search via backend,
 * block/unblock with Swal feedback, applyDateFilter client-side,
 * gotoDetails → onAssegnaDet modal.
 */
@Component({
  selector: 'app-ordini-parziali-list',
  templateUrl: './ordini-parziali-list.component.html',
  styleUrls: ['./ordini-parziali-list.component.scss'],
  standalone: false,
})


export class OrdiniParzialiListComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly iconService = inject(IconsService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterService = inject(SearchService);
  public readonly modalService = inject(OpenModalService);

  // ── Data ────────────────────────────────────────────────────────
  public gridData: Orders.IOrderNewList[] = [];
  public displayData: Orders.IOrderNewList[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public selectedCustomer: string | null = null;
  public selectedOrderId = -1;
  public readonly showAll = 'Mostra tutti';
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ── Date range filter (defaults: 2021-01-01 → today) ───────────
  startDate: Date = new Date(2021, 0, 1);
  endDate: Date = new Date();

  filters = {
    dateOrginizedBy: {
      text: 'Data ordine',
      value: 'or_order_number',
    },
    startingDate: this.startDate as Date,
    endDate: this.endDate as Date,
    minDate: new Date(),
    maxDate: new Date(),
  };

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.relodusFunctione();
  }

  /** Original method name preserved (legacy spelling) because other
   *  files may grep for it — internal only, callable as init handler. */
  relodusFunctione(): void {
    this.loading = true;
    this.filterTerm = this.filterService.getFilterText() ?? undefined;

    if (this.filterTerm) {
      this.searchOrders(this.filterTerm);
      return;
    }

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.getOrdineParziali();
        }
        else {
          const searchParam: string = params['search'] ?? '';
          const dateParam: string = params['data'];
          this.filters.startingDate = dateParam
            ? parseISO(dateParam)
            : this.startDate;
          this.searchOrders(searchParam);
          this.loading = false;
        }
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  getOrdineParziali(): void {
    this.loading = true;
    this.ordersServ
      .getNewOrdersPartial()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (openOrdersData) => {
          this.gridData = openOrdersData?.data ?? [];
          this.loading = false;
          this.applyDateFilter();
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Search.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(searchString?: string): void {
    if (searchString !== undefined) this.filterTerm = searchString;
    if (!this.filterTerm || this.filterTerm.trim() === '') {
      this.getOrdineParziali();
      return;
    }
    this.ordersServ
      .searchPartialOrders(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          if (result?.error_message?.msg_code === 8) {
            this.gridData = [];
          } 
          else {
            this.gridData = result?.data ?? [];
          }
          this.loading = false;
          this.applyDateFilter();
          this.cdr.detectChanges();
        },
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.startDate;
    this.filterService.clearFilterText();
    this.getOrdineParziali();
  }

  handleFilterChange(): void {
    this.filterService.setFilterText(this.filterTerm ?? '');
  }

  onInputChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.validity && inputElement.validity.valueMissing) {
      this.filters.startingDate = this.startDate;
      this.getOrdineParziali();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Date range — client-side filter on gridData.
  // ──────────────────────────────────────────────────────────────────

  applyDateFilter(): void {
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    this.displayData = this.gridData.filter((item) => {
      if (!item.or_order_date) return true;
      try {
        const d = parseISO(item.or_order_date);
        if (isNaN(d.getTime())) return true;
        return (
          (isAfter(d, start) || isEqual(d, start)) &&
          (isBefore(d, end) || isEqual(d, end))
        );
      } catch {
        return true;
      }
    });
    this.cdr.detectChanges();
  }

  onStartDateChange(value: Date | string | null): void {
    this.filters.startingDate = !value
      ? this.startDate
      : value instanceof Date
        ? value
        : new Date(value);
    this.applyDateFilter();
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
    this.applyDateFilter();
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail flow — click row → open detail modal (E.2).
  // ──────────────────────────────────────────────────────────────────

  gotoDetails(order_id: number): void {
    this.selectedOrderId = order_id;
    this.modalService.onAssegnaDet();
  }

  /** Called by the detail modal via (result) event when it closes. */
  onChildClose(result: unknown): void {
    if (result) {
      this.relodusFunctione();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Block / unblock actions.
  // ──────────────────────────────────────────────────────────────────

  block(order: Orders.IOrderNewList): void {
    this.iconService.changeIconToBlocked(order);
    this.ordersServ
      .lockNewOrder(String(order.or_gamma_id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) return;
          this.getOrdineParziali();
          Swal.fire({
            icon: 'success',
            title: 'Bloccato!',
            text: 'Ordine bloccato con successo.',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
      });
  }

  unblock(order: Orders.IOrderNewList): void {
    this.iconService.changeIconToWorking(order);
    this.ordersServ
      .unlockNewOrder(String(order.or_gamma_id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) return;
          this.getOrdineParziali();
          Swal.fire({
            icon: 'success',
            title: 'Sbloccato!',
            text: 'Ordine parziale sbloccato con successo.',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // UI helpers.
  // ──────────────────────────────────────────────────────────────────

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  isBlocked(order: Orders.IOrderNewList): boolean {
    return order?.or_status === 'B';
  }

  get filteredOrders(): Orders.IOrderNewList[] {
    const customer = this.selectedCustomer;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.gridData.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if (start && o.or_order_date && new Date(o.or_order_date) < start) {
        return false;
      }
      if (end && o.or_order_date && new Date(o.or_order_date) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
